import { EnumFilter } from "@/components/data-table/components/EnumFilter";
import StandardQueryTable from "@/components/StandardQueryTable";
import { Module, PATH_AGREEMENT } from "@/constants";
import { readAgreement } from "@/schemas/agreement";
import { fetchAgreementsList } from "@/services/agreements";
import { AGREEMENT_STATUS, ReadAgreement } from "@/types/agreement";
import { useMemo } from "react";
import { generatePath } from "react-router-dom";
import TaxStatus from "@/components/TaxStatus";
import { AgreementStatusBadge } from "./AgreementStatusBadge";
import { useFormatters } from "@/lib/hooks/useFormatters";

const AgreementsList = () => {
  const { formatBoolean } = useFormatters();
  const columnOverrides = useMemo(
    () => ({
      agreement_status: {
        accessorKey: "agreement_status",
        cell: ({ row }: { row: { original: ReadAgreement } }) => (
          <div className="flex items-center h-full">
            <AgreementStatusBadge
              className="text-xs"
              status={row.original.agreement_status}
            />
          </div>
        ),
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: AGREEMENT_STATUS,
              translationKey: "agreementStatuses",
            },
          },
        },
      },
      agreement_tax_status: {
        accessorKey: "agreement_tax_status",
        cell: ({ row }: { row: { original: ReadAgreement } }) => (
          <div className="flex items-center h-full">
            <TaxStatus
              className="text-xs"
              value={row.original.agreement_tax_status ?? undefined}
            />
          </div>
        ),
      },
      statements_up_to_date: {
        accessorKey: "statements_up_to_date",
        header: "agreements.fields.statements_up_to_date.label",
        cell: ({ row }: { row: { original: ReadAgreement } }) =>
          formatBoolean(
            row.original.statements_up_to_date,
            "agreements.fields.statements_up_to_date",
          ),
      },
    }),
    [formatBoolean],
  );

  const initialColumnVisibility = useMemo(
    () => ({
      contractor_last_name: false,
      contractor_first_name: false,
      statements_up_to_date: false,
      statements_confirmed_by_contractor_at: false,
      contractor_registered_by: false,
      contractor_registered_by_name: false,
      agreement_sign_date: false,
      city: false,
      postal_code: false,
      post_office: false,
      street: false,
      house_number: false,
      apartment_number: false,
      province_name: false,
      province_nfz_code: false,
      county: false,
      municipality: false,
      birth_date: false,
      birth_place: false,
      gender: false,
      bank_account_number: false,
      tax_office_name: false,
      citizenship_name: false,
      disability_certificate_code: false,
      rodo_accepted: false,
      regulations_accepted: false,
      is_student: false,
      school_name: false,
      student_id_number: false,
      student_id_valid_until: false,
      is_employed: false,
      employer_name: false,
      employment_certificate_issue_date: false,
      voluntary_sickness_insurance: false,
      is_pensioner: false,
      pension_number: false,
      pension_start_date: false,
      pension_office_name: false,
      is_disability_pensioner: false,
      disability_pension_number: false,
      disability_pension_start_date: false,
      disability_pension_office_name: false,
      disability_pension_type: false,
      has_valid_visa: false,
      visa_number: false,
      visa_valid_until: false,
      has_polish_card: false,
      polish_card_valid_until: false,
      has_residence_card: false,
      residence_card_valid_until: false,
      watched_instructional_videos: false,
      agreement_tax_status: false,
      recruitment_responsible_user_full_name: false,
      recruitment_responsible_user_role: false,
    }),
    [],
  );

  return (
    <StandardQueryTable
      schema={readAgreement}
      name="agreements"
      fetchList={fetchAgreementsList}
      pathBuilder={(agreement) =>
        generatePath(PATH_AGREEMENT, {
          agreementId: agreement.id,
        })
      }
      columnOverrides={columnOverrides}
      tableFeatures={{
        exportSettings: { moduleName: Module.Agreements },
        initialColumnVisibility,
      }}
    />
  );
};

export default AgreementsList;
